/**
 * Maps Add Program UI form state to Partner Central POST bodies.
 * UI keeps human-readable labels; wire values follow ui-integration-guide.md.
 */

const parseNumber = (value) => {
  const cleaned = String(value || "").replace(/[$,%/]/g, "").trim();
  if (!cleaned) return null;
  const amount = Number(cleaned);
  return Number.isFinite(amount) ? amount : NaN;
};

const toMoney = (value) => {
  const amount = parseNumber(value);
  if (amount == null || Number.isNaN(amount)) return undefined;
  return amount.toFixed(2);
};

const toPercent = (value) => {
  const amount = parseNumber(value);
  if (amount == null || Number.isNaN(amount)) return undefined;
  return amount.toFixed(2);
};

const toDays = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return undefined;
  if (!/^\d+$/.test(raw)) return undefined;
  return Number(raw);
};

const paymentTermsDays = (label) => {
  const match = String(label || "").match(/^Net\s+(\d+)$/i);
  return match ? Number(match[1]) : undefined;
};

const termLengthMonths = (label) => {
  if (label === "1-Year") return 12;
  if (label === "3-Year") return 36;
  return undefined;
};

/** UI program type code → API program_type (RI → RI_SP). */
export const toApiProgramType = (uiType) => (uiType === "RI" ? "RI_SP" : uiType);

const PARTNER_TIER = {
  "Tier 1 Registered": "TIER_1_REGISTERED",
  "Tier 2 Advanced": "TIER_2_ADVANCED",
  Premier: "PREMIER",
};

const GROWTH_TRIGGER = {
  "Spend Target": "SPEND_TARGET",
  "Workload Modernization (Bedrock/Containers)": "WORKLOAD_MODERNIZATION",
  "Migration Count": "MIGRATION_COUNT",
};

const PAYOUT_STRUCTURE = {
  "Retroactive Discount": "RETROACTIVE_DISCOUNT",
  "Incremental Acceleration": "INCREMENTAL_ACCELERATION",
  "Back-End Rebate Credit": "BACK_END_REBATE_CREDIT",
};

const TRUE_UP_MODEL = {
  "Annual True-Up": "ANNUAL_TRUE_UP",
  "End-of-Term True-Up": "END_OF_TERM_TRUE_UP",
  "True-Up Billing Mechanism": "TRUE_UP_BILLING_MECHANISM",
};

const TRUE_UP_WINDOW = {
  "30 days after the period ends": 30,
  "60 days after the period ends": 60,
  "90 days after the period ends": 90,
};

const SHORTFALL_RULE = {
  "Invoice True-Up Charge": "INVOICE_TRUE_UP_CHARGE",
  "Rollover to Next Year": "ROLLOVER_TO_NEXT_YEAR",
};

const RI_COMMITMENT = {
  "Standard RI": "STANDARD_RI",
  "Convertible RI": "CONVERTIBLE_RI",
  "Compute Savings Plan / RI Blended": "COMPUTE_SAVINGS_PLAN_RI_BLENDED",
};

const PAYMENT_OPTION = {
  "All Upfront": "ALL_UPFRONT",
  "Partial Upfront": "PARTIAL_UPFRONT",
  "No Upfront": "NO_UPFRONT",
};

const AMORTIZATION = {
  "Straight-Line Monthly": "STRAIGHT_LINE_MONTHLY",
  "Cash-Flow Realized": "CASH_FLOW_REALIZED",
};

const milestoneUnitForMetric = (metricCode) => {
  if (metricCode === "SPEND_TARGET") return "USD";
  if (metricCode === "MIGRATION_COUNT" || metricCode === "WORKLOAD_MODERNIZATION") return "WORKLOADS";
  return undefined;
};

const distinctAccountIds = (values) => [
  ...new Set(
    (values || [])
      .map((id) => String(id || "").trim())
      .filter((id) => /^\d{12}$/.test(id))
  ),
];

/**
 * Normalize a payer → payees map for the wire (12-digit ids).
 * Keeps an explicit payer id in its own values array when the user selected that
 * payer as a Selected Account chip (e.g. "332…": ["332…", "payee…"]).
 * @returns {Record<string, string[]>|undefined}
 */
const sanitizePayerPayeeMap = (raw) => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const cleaned = {};
  Object.entries(raw).forEach(([payer, payees]) => {
    const payerId = String(payer || "").trim();
    if (!/^\d{12}$/.test(payerId)) return;
    const list = distinctAccountIds(payees);
    if (list.length) cleaned[payerId] = list;
  });
  return Object.keys(cleaned).length ? cleaned : undefined;
};

/**
 * Build payer_payee_accounts as documented:
 *   { "<payer>": ["<payee>", ...] }
 *
 * Prefers the map maintained by the accounts field (current UI selection).
 * Falls back to reconstructing from payer/payee arrays + linkage.
 *
 * @returns {Record<string, string[]>|undefined}
 */
const payerPayeeAccounts = (form) => {
  // Source of truth from the accounts field — always reflects what the user just selected.
  const fromField = sanitizePayerPayeeMap(form.payerPayeeAccountsMap);
  if (fromField) return fromField;

  const selectedPayers = distinctAccountIds(form.payerAccountNumbers);
  const selectedPayees = distinctAccountIds(form.payeeAccountNumbers).filter(
    (id) => !selectedPayers.includes(id)
  );
  if (!selectedPayers.length && !selectedPayees.length) return undefined;

  const payerByPayee = {
    ...(form.payeePayerByNumber && typeof form.payeePayerByNumber === "object"
      ? form.payeePayerByNumber
      : {}),
    ...(form.accountPayerByNumber && typeof form.accountPayerByNumber === "object"
      ? form.accountPayerByNumber
      : {}),
  };

  const ownerOf = (accountId) => {
    const owner = String(payerByPayee[accountId] || "").trim();
    return /^\d{12}$/.test(owner) ? owner : "";
  };

  // Keys = the user's current payer selection only (never resurrect removed payers).
  let payerKeys = selectedPayers.filter((id) => {
    const owner = ownerOf(id);
    return !owner || owner === id || !selectedPayers.includes(owner);
  });

  if (!payerKeys.length && selectedPayees.length) {
    const derived = new Set();
    selectedPayees.forEach((payee) => {
      const owner = ownerOf(payee);
      derived.add(owner || payee);
    });
    payerKeys = distinctAccountIds([...derived]);
  }

  payerKeys = distinctAccountIds(payerKeys);
  if (!payerKeys.length) return undefined;

  const map = {};
  payerKeys.forEach((payer) => {
    map[payer] = [];
  });

  // Explicit Selected Accounts "Payer:" chips → include payer id in its values array.
  distinctAccountIds(form.payeeUiPayerNumbers).forEach((payer) => {
    if (map[payer] && !map[payer].includes(payer)) map[payer].push(payer);
  });

  // Always place every selected payee under a current payer key (never drop → omit field).
  selectedPayees.forEach((payee) => {
    const owner = ownerOf(payee);
    const key =
      (owner && map[owner] && owner) ||
      (payerKeys.length === 1 ? payerKeys[0] : null) ||
      payerKeys[0];
    if (!key || !map[key]) return;
    if (!map[key].includes(payee)) map[key].push(payee);
  });

  return sanitizePayerPayeeMap(map);
};

const distinctStrings = (values) => {
  if (!Array.isArray(values) || !values.length) return undefined;
  const next = [...new Set(values.map((v) => String(v || "").trim()).filter(Boolean))];
  return next.length ? next : undefined;
};

const volumeTiers = (tiers) => {
  if (!Array.isArray(tiers) || !tiers.length) return [];
  return tiers
    .filter((tier) => tier.name?.trim() || tier.startDate || tier.endDate || tier.threshold?.trim() || tier.discount?.trim())
    .map((tier) => ({
      tier_name: String(tier.name || "").trim(),
      start_date: tier.startDate || undefined,
      end_date: tier.endDate || undefined,
      minimum_spend_threshold: toMoney(tier.threshold),
      discount_percent: toPercent(tier.discount),
    }));
};

const setIf = (body, key, value) => {
  if (value === undefined || value === null || value === "") return;
  body[key] = value;
};

const commonBody = (form, status) => {
  const body = {
    program_type: toApiProgramType(form.programType),
    status: status === "draft" ? "DRAFT" : "ACTIVE",
  };
  setIf(body, "program_display_name", String(form.displayName || "").trim());
  setIf(body, "agreement_id", String(form.agreementId || "").trim());
  setIf(body, "reference_number", String(form.referenceNumber || "").trim());
  const accounts = payerPayeeAccounts(form);
  if (accounts) body.payer_payee_accounts = accounts;
  const regions = distinctStrings(form.regions);
  if (regions) body.assigned_regions = regions;
  setIf(body, "start_date", form.startDate || undefined);
  setIf(body, "end_date", form.endDate || undefined);
  return body;
};

const buildPpa = (form, status) => {
  const body = commonBody(form, status);
  const services = distinctStrings(form.eligibleServices);
  if (services) body.eligible_services = services;
  setIf(body, "renewal_notification_days", toDays(form.renewalNotification));
  setIf(body, "baseline_distribution_discount_percent", toPercent(form.baselineDiscount));
  setIf(body, "annual_commitment", toMoney(form.annualCommitment));
  setIf(body, "payment_terms_days", paymentTermsDays(form.paymentTerms));
  body.volume_tier_discounts = volumeTiers(form.tiers);
  return body;
};

const buildSpp = (form, status) => {
  const body = commonBody(form, status);
  const services = distinctStrings(form.eligibleServices);
  if (services) body.eligible_services = services;
  setIf(body, "renewal_notification_days", toDays(form.renewalNotification));
  setIf(body, "partner_tier_level", PARTNER_TIER[form.partnerTierLevel]);
  setIf(body, "baseline_partner_discount_percent", toPercent(form.baselineDiscount));
  setIf(body, "annual_spend_commitment", toMoney(form.annualCommitment));
  body.volume_tier_discounts = volumeTiers(form.tiers);

  const trigger = GROWTH_TRIGGER[form.growthTriggerMetric];
  setIf(body, "growth_trigger_metric", trigger);
  const milestoneAmount = toMoney(form.targetMilestoneValue) ?? (() => {
    const amount = parseNumber(form.targetMilestoneValue);
    return amount == null || Number.isNaN(amount) ? undefined : String(amount);
  })();
  setIf(body, "target_milestone_value", milestoneAmount);
  if (milestoneAmount !== undefined) {
    setIf(body, "target_milestone_unit", milestoneUnitForMetric(trigger));
  }
  setIf(body, "growth_bonus_rate_percent", toPercent(form.growthBonusRate));
  setIf(body, "payout_structure", PAYOUT_STRUCTURE[form.payoutStructure]);
  const qualifying = distinctStrings(form.qualifyingServices);
  if (qualifying) body.qualifying_target_services = qualifying;
  if (typeof form.expirationOverride === "boolean") {
    body.contract_expiration_override_enabled = form.expirationOverride;
  }
  return body;
};

const buildEdp = (form, status) => {
  const body = commonBody(form, status);
  setIf(body, "renewal_notification_days", toDays(form.renewalNotification));
  setIf(body, "true_up_window_days", TRUE_UP_WINDOW[form.trueUpWindow]);
  setIf(body, "total_multi_year_commitment", toMoney(form.multiYearCommitment));
  setIf(body, "annual_consumption_target", toMoney(form.annualConsumptionTarget));
  setIf(body, "edp_discount_percent", toPercent(form.edpDiscount));
  setIf(body, "payment_terms_days", paymentTermsDays(form.paymentTerms));
  setIf(body, "commitment_true_up_model", TRUE_UP_MODEL[form.trueUpModel]);
  setIf(body, "shortfall_penalty_rule", SHORTFALL_RULE[form.shortfallRule]);
  body.rollover_unused_commitment = Boolean(form.rolloverUnused);
  const services = distinctStrings(form.eligibleServices);
  if (services) body.eligible_services = services;
  return body;
};

const buildRi = (form, status) => {
  const body = commonBody(form, status);
  setIf(body, "expiration_alert_days", toDays(form.expirationAlertWindow));
  setIf(body, "ri_commitment_type", RI_COMMITMENT[form.riCommitmentType]);
  setIf(body, "payment_option", PAYMENT_OPTION[form.paymentOption]);
  setIf(body, "term_length_months", termLengthMonths(form.termLength));
  setIf(body, "amortization_tracking_method", AMORTIZATION[form.amortizationMethod]);
  setIf(body, "target_coverage_percent", toPercent(form.targetCoverage));
  setIf(body, "waste_threshold_alert_percent", toPercent(form.wasteThreshold));
  const riServices = distinctStrings(form.riEligibleServices);
  if (riServices) body.ri_eligible_services = riServices;
  if (typeof form.crossAccountSharing === "boolean") {
    body.cross_account_sharing_enabled = form.crossAccountSharing;
  }
  return body;
};

/**
 * @param {object} form Add Program form state
 * @param {"draft"|"saved"} status UI save mode
 */
export const buildProgramCreatePayload = (form, status) => {
  switch (form.programType) {
    case "PPA":
      return buildPpa(form, status);
    case "SPP":
      return buildSpp(form, status);
    case "EDP":
      return buildEdp(form, status);
    case "RI":
      return buildRi(form, status);
    default:
      return commonBody(form, status);
  }
};

const invertMap = (map) =>
  Object.fromEntries(Object.entries(map).map(([label, code]) => [code, label]));

const LABEL_PARTNER_TIER = invertMap(PARTNER_TIER);
const LABEL_GROWTH_TRIGGER = invertMap(GROWTH_TRIGGER);
const LABEL_PAYOUT_STRUCTURE = invertMap(PAYOUT_STRUCTURE);
const LABEL_TRUE_UP_MODEL = invertMap(TRUE_UP_MODEL);
const LABEL_TRUE_UP_WINDOW = invertMap(TRUE_UP_WINDOW);
const LABEL_SHORTFALL_RULE = invertMap(SHORTFALL_RULE);
const LABEL_RI_COMMITMENT = invertMap(RI_COMMITMENT);
const LABEL_PAYMENT_OPTION = invertMap(PAYMENT_OPTION);
const LABEL_AMORTIZATION = invertMap(AMORTIZATION);

const asText = (value) => (value == null ? "" : String(value));

const formatMoneyField = (value) => {
  if (value == null || value === "") return "";
  const amount = parseNumber(value);
  if (amount == null || Number.isNaN(amount)) return asText(value);
  return amount.toLocaleString("en-US", { maximumFractionDigits: 2 });
};

const formatPercentField = (value) => {
  if (value == null || value === "") return "";
  const amount = parseNumber(value);
  if (amount == null || Number.isNaN(amount)) return asText(value);
  return String(amount);
};

const paymentTermsLabel = (days) => {
  if (days == null || days === "") return "";
  const n = Number(days);
  return Number.isFinite(n) ? `Net ${n}` : "";
};

const termLengthLabel = (months) => {
  if (Number(months) === 12) return "1-Year";
  if (Number(months) === 36) return "3-Year";
  return "";
};

const mapVolumeTiers = (rows) => {
  if (!Array.isArray(rows) || !rows.length) return [];
  return rows.map((row, index) => ({
    id: `tier-loaded-${index}`,
    name: asText(row.tier_name),
    startDate: row.start_date || "",
    endDate: row.end_date || "",
    threshold: formatMoneyField(row.minimum_spend_threshold),
    discount: formatPercentField(row.discount_percent),
  }));
};

const mapAnnualCommitmentBreakdown = (rows) => {
  if (!Array.isArray(rows) || !rows.length) return [];
  return rows.map((row, index) => ({
    id: `year-loaded-${index}`,
    label: asText(row.year_label),
    startDate: row.start_date || "",
    endDate: row.end_date || "",
    commitment: formatMoneyField(row.commitment_amount),
    discount: formatPercentField(row.discount_percent),
  }));
};

/** API program_type → UI programType code (RI_SP → RI). */
export const toUiProgramType = (apiType) => (apiType === "RI_SP" ? "RI" : apiType || "EDP");

/**
 * Maps a Partner Central program GET/list record into Add Program form state.
 */
export const formFromProgramRecord = (record) => {
  // Prefer request/response field; fall back to alternate map key if API returns it.
  const rawMap =
    (record?.payer_payee_accounts && typeof record.payer_payee_accounts === "object"
      ? record.payer_payee_accounts
      : null) ||
    (record?.payer_payee_map && typeof record.payer_payee_map === "object"
      ? record.payer_payee_map
      : null) ||
    {};
  const map = rawMap;
  const payerAccountNumbers = Object.keys(map)
    .map((id) => String(id || "").trim())
    .filter((id) => /^\d{12}$/.test(id));
  const payeePayerByNumber = {};
  const payeeUiPayerNumbers = [];
  const payeeAccountNumbers = [
    ...new Set(
      Object.entries(map).flatMap(([payer, payees]) => {
        const payerId = String(payer || "").trim();
        const ids = (Array.isArray(payees) ? payees : [])
          .map((id) => String(id || "").trim())
          .filter((id) => /^\d{12}$/.test(id));
        // Payer listed in its own values → restore as Selected Accounts "Payer:" chip.
        if (/^\d{12}$/.test(payerId) && ids.includes(payerId)) {
          payeeUiPayerNumbers.push(payerId);
        }
        return ids
          .filter((id) => id !== payerId && !payerAccountNumbers.includes(id))
          .map((id) => {
            if (/^\d{12}$/.test(payerId)) payeePayerByNumber[id] = payerId;
            return id;
          });
      })
    ),
  ];
  const payerPayeeAccountsMap = sanitizePayerPayeeMap(map) || {};
  const shortfallFromApi = () => {
    if (record.shortfall_penalty_rule && LABEL_SHORTFALL_RULE[record.shortfall_penalty_rule]) {
      return LABEL_SHORTFALL_RULE[record.shortfall_penalty_rule];
    }
    if (record.rollover_unused_commitment === true) return "Rollover to Next Year";
    if (record.rollover_unused_commitment === false) return "Invoice True-Up Charge";
    return "";
  };

  const uiType = toUiProgramType(record.program_type);
  const baselineDiscount =
    uiType === "SPP"
      ? formatPercentField(record.baseline_partner_discount_percent)
      : formatPercentField(record.baseline_distribution_discount_percent);
  const annualCommitment =
    uiType === "SPP"
      ? formatMoneyField(record.annual_spend_commitment)
      : formatMoneyField(record.annual_commitment);

  return {
    programType: uiType,
    displayName: asText(record.program_display_name),
    agreementId: asText(record.agreement_id),
    referenceNumber: asText(record.reference_number),
    payerAccountNumbers,
    payeeAccountNumbers,
    payeeUiPayerNumbers: [...new Set(payeeUiPayerNumbers)],
    payerPayeeAccountsMap,
    payeePayerByNumber,
    regions: Array.isArray(record.assigned_regions) ? [...record.assigned_regions] : [],
    eligibleServices: Array.isArray(record.eligible_services) ? [...record.eligible_services] : [],
    qualifyingServices: Array.isArray(record.qualifying_target_services)
      ? [...record.qualifying_target_services]
      : [],
    riEligibleServices: Array.isArray(record.ri_eligible_services)
      ? [...record.ri_eligible_services]
      : [],
    startDate: record.start_date || "",
    endDate: record.end_date || "",
    renewalNotification:
      record.renewal_notification_days != null ? String(record.renewal_notification_days) : "",
    expirationAlertWindow:
      record.expiration_alert_days != null ? String(record.expiration_alert_days) : "",
    baselineDiscount,
    annualCommitment,
    paymentTerms: paymentTermsLabel(record.payment_terms_days),
    partnerTierLevel: LABEL_PARTNER_TIER[record.partner_tier_level] || "",
    growthTriggerMetric: LABEL_GROWTH_TRIGGER[record.growth_trigger_metric] || "",
    targetMilestoneValue: formatMoneyField(record.target_milestone_value) || asText(record.target_milestone_value),
    growthBonusRate: formatPercentField(record.growth_bonus_rate_percent),
    payoutStructure: LABEL_PAYOUT_STRUCTURE[record.payout_structure] || "",
    expirationOverride: Boolean(record.contract_expiration_override_enabled),
    trueUpWindow: LABEL_TRUE_UP_WINDOW[record.true_up_window_days] || "",
    multiYearCommitment: formatMoneyField(record.total_multi_year_commitment),
    annualConsumptionTarget: formatMoneyField(record.annual_consumption_target),
    edpDiscount: formatPercentField(record.edp_discount_percent),
    trueUpModel: LABEL_TRUE_UP_MODEL[record.commitment_true_up_model] || "",
    shortfallRule: shortfallFromApi(),
    rolloverUnused: Boolean(record.rollover_unused_commitment),
    riCommitmentType: LABEL_RI_COMMITMENT[record.ri_commitment_type] || "",
    paymentOption: LABEL_PAYMENT_OPTION[record.payment_option] || "",
    termLength: termLengthLabel(record.term_length_months),
    amortizationMethod: LABEL_AMORTIZATION[record.amortization_tracking_method] || "",
    targetCoverage: formatPercentField(record.target_coverage_percent),
    wasteThreshold: formatPercentField(record.waste_threshold_alert_percent),
    crossAccountSharing: Boolean(record.cross_account_sharing_enabled),
    years: mapAnnualCommitmentBreakdown(record.annual_commitment_breakdown),
    tiers: mapVolumeTiers(record.volume_tier_discounts),
  };
};

/** Client-side ACTIVE completeness aligned with the integration guide. */
export const validateActiveProgram = (form) => {
  const next = {};
  if (!String(form.displayName || "").trim()) next.displayName = "Enter a program display name.";
  if (!String(form.agreementId || "").trim()) next.agreementId = "Enter an agreement ID.";
  if (!form.startDate) next.startDate = "Select a start date.";
  if (!form.endDate) next.endDate = "Select an end date.";
  if (form.startDate && form.endDate && form.endDate < form.startDate) {
    next.endDate = "End date must be on or after the start date.";
  }

  if (form.programType === "PPA") {
    if (!String(form.baselineDiscount || "").trim()) next.baselineDiscount = "Enter a baseline discount.";
    if (!String(form.annualCommitment || "").trim()) next.annualCommitment = "Enter a commitment amount.";
  }
  if (form.programType === "SPP") {
    if (!form.partnerTierLevel) next.partnerTierLevel = "Select a partner tier level.";
    if (!String(form.baselineDiscount || "").trim()) next.baselineDiscount = "Enter a baseline discount.";
    if (!String(form.annualCommitment || "").trim()) next.annualCommitment = "Enter a spend commitment.";
    if (String(form.targetMilestoneValue || "").trim()) {
      const amount = parseNumber(form.targetMilestoneValue);
      if (amount == null || Number.isNaN(amount) || amount < 0) {
        next.targetMilestoneValue = "Enter a numeric milestone value.";
      }
    }
  }
  if (form.programType === "EDP") {
    if (!String(form.multiYearCommitment || "").trim()) next.multiYearCommitment = "Enter a multi-year commitment.";
    if (!String(form.annualConsumptionTarget || "").trim()) next.annualConsumptionTarget = "Enter an annual consumption target.";
    if (!String(form.edpDiscount || "").trim()) next.edpDiscount = "Enter the EDP discount.";
    if (!form.trueUpWindow) next.trueUpWindow = "Select a true-up window.";
    if (!form.trueUpModel) next.trueUpModel = "Select a true-up model.";
    if (!form.shortfallRule) next.shortfallRule = "Select a shortfall rule.";
  }
  if (form.programType === "RI") {
    if (!form.riCommitmentType) next.riCommitmentType = "Select an RI commitment type.";
    if (!form.paymentOption) next.paymentOption = "Select a payment option.";
    if (!form.termLength) next.termLength = "Select a term length.";
    if (!form.amortizationMethod) next.amortizationMethod = "Select an amortization method.";
    if (!String(form.targetCoverage || "").trim()) next.targetCoverage = "Enter a target coverage percentage.";
    if (!String(form.wasteThreshold || "").trim()) next.wasteThreshold = "Enter a waste threshold percentage.";
  }

  return next;
};
